import React, { useContext, useEffect, useState } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import StyledTableRow from 'shared/components/table/StyledTableRow';
import Paper from '@mui/material/Paper';
import CallMergeIcon from '@mui/icons-material/CallMerge';
import { Link } from 'react-router-dom';
import { Button, Dialog } from '@mui/material';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import { dateTimeString, truncateString } from 'shared/utils';
import { VARIANTS } from 'shared/constants';
import { useAuth, useNotification, useRequest } from 'shared/hooks';
import { addMergedTicket, getEntry, searchTickets } from 'shared/api/support';
import { getTicketPath } from 'shared/routes/support';
import { SideNavContext } from 'shared/hoc/withSideNav';
import { TooltipIconButton } from 'shared/components/abstract/interactables';

/**
 * @param {Object} props
 * @param {Object} props.ticket
 * @param {Function} props.setEntries
 * @param {number} props.parentTicketId
 * @param {string} props.parentTicketTitle
 */
function Row(props) {
  const {
    ticket, setEntries, parentTicketId, parentTicketTitle,
  } = props;

  return (
    (ticket.id !== parentTicketId)
    && (
      <StyledTableRow>
        <TableCell><Link to={getTicketPath(ticket.id)}>{ticket.id}</Link></TableCell>
        <TableCell style={{ width: '35%' }}>{truncateString(ticket.title, 64)}</TableCell>
        <TableCell style={{ width: '8%' }}>{ticket.priority}</TableCell>
        <TableCell style={{ width: '175px' }}>
          {dateTimeString(ticket.created_at)}
        </TableCell>
        <TableCell style={{ width: '8%' }}>{ticket.status}</TableCell>
        <TableCell style={{ width: '10%' }}>{ticket.ticket_assignment.assignee_username}</TableCell>
        <TicketMergeModal
          ticket={ticket}
          setEntries={setEntries}
          parentTicketId={parentTicketId}
          parentTicketTitle={parentTicketTitle}
        />
      </StyledTableRow>
    ));
}

const columns = [
  {
    name: 'Ticket',
    jsonKey: 'id',
  },
  {
    name: 'Title',
    jsonKey: 'title',
  },
  {
    name: 'Priority',
    jsonKey: 'priority',
  },
  {
    name: 'Created At',
    jsonKey: 'created_at',
  },
  {
    name: 'Status',
    jsonKey: 'status',
  },
  {
    name: 'Assigned To',
    jsonKey: 'assigned_to',
  },
  {
    name: 'Merge',
    jsonKey: 'merge',
  },
];

/**
 * @param {Object} props
 * @param {string} props.searchQuery
 * @param {Function} props.setEntries
 * @param {number} props.parentTicketId
 * @param {string} props.parentTicketTitle
 */
export default function TicketMergeTable(props) {
  const {
    searchQuery,
    setEntries,
    parentTicketId,
    parentTicketTitle,
  } = props;

  const { request, isLoading } = useRequest();
  const [tickets, setTickets] = useState([]);

  useEffect(() => {
    (async () => {
      const result = await request(searchTickets(searchQuery));
      if (!result) return;
      setTickets(result.rows);
    })();
  }, [request, searchQuery, setEntries]);

  return (
    !tickets || tickets.length === 0 || (tickets.length === 1 && tickets[0].id === parentTicketId)
      ? (
        <div style={{ paddingLeft: 10 }}>
          <strong>{isLoading ? 'Loading...' : 'No Ticket Entries'}</strong>
        </div>
      )
      : (
        <TableContainer className="table-height-md" component={Paper}>
          <Table stickyHeader aria-label="collapsible table">
            <TableHead>
              <TableRow>
                {columns.map(({ name, jsonKey }) => (
                  <TableCell className="row-font" key={jsonKey}>{name}</TableCell>
                ))}
                <TableCell size="small" style={{ width: 35, padding: 10 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {tickets.map((ticket) => (
                <Row
                  key={ticket.id}
                  ticket={ticket}
                  setEntries={setEntries}
                  parentTicketId={parentTicketId}
                  parentTicketTitle={parentTicketTitle}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )
  );
}

/**
 * @param {Object} props
 * @param {Object} props.ticket
 * @param {number} props.parentTicketId
 * @param {string} props.parentTicketTitle
 * @param {Function} props.setEntries
 */
export function TicketMergeModal(props) {
  const {
    ticket, parentTicketId, parentTicketTitle, setEntries,
  } = props;
  const [show, setShow] = useState(false);
  const { request } = useRequest();
  const { user } = useAuth();
  const notification = useNotification();
  const syncSideNav = useContext(SideNavContext);

  const submitTicketMerge = async () => {
    // create the merged ticket and then retrieve its corresponding entry
    const mergedTicketParams = {
      ticket_id: parentTicketId,
      merge_ticket_id: ticket.id,
      username: user.username,
    };
    const mergedTicketResult = await request(addMergedTicket(mergedTicketParams));
    const mergedEntry = await request(getEntry(mergedTicketResult.id));

    if (!mergedTicketResult) {
      notification.pushError(`Failed to merge Ticket ${ticket.id}: ${ticket.title}`);
      return;
    }
    setEntries(mergedEntry);
    syncSideNav(getTicketPath(ticket.id));
    notification.pushSuccess(`Merged Ticket ${ticket.id}: ${ticket.title}, into Ticket ${parentTicketId} successfully`);
    setShow(false);
  };

  return (
    <>
      <TableCell style={{ width: '5%', paddingRight: 10, paddingLeft: 0 }}>
        <TooltipIconButton
          className="entry-icon"
          message="Merge ticket"
          icon={CallMergeIcon}
          onClick={() => setShow(true)}
        />
      </TableCell>
      <Dialog
        open={show}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {dateTimeString(ticket.created_at)}
          &nbsp;&nbsp;
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center', wordWrap: 'break-word' }}>
          <DialogContentText component="span">
            {`Merge Ticket ${ticket.id}:`}
            <br />
            {`${truncateString(ticket.title, 64)}`}
            <br />
            INTO
            <br />
            {`Ticket ${parentTicketId}:`}
            <br />
            {`${truncateString(parentTicketTitle, 64)}?`}
            <br />
            <br />
            <strong>This action cannot be undone.</strong>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            variant={VARIANTS.outlined}
            color={VARIANTS.primary}
            onClick={() => setShow(false)}
          >
            Close
          </Button>
          <br />
          <Button
            variant={VARIANTS.contained}
            color={VARIANTS.primary}
            onClick={() => submitTicketMerge()}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
